/**
 * Persistent document model. Everything in this file is plain JSON-serialisable
 * data: no class instances, DOM objects or functions. A `Project` is one "scene
 * collection" and is treated as immutable; all changes go through the project
 * store (state/api.ts) so they are undoable and observable by reference equality.
 */

export type Id = string;

export interface Vec2 {
  x: number;
  y: number;
}

/** JSON-serialisable settings bag owned by a source/filter/transition type. */
export type SettingsBag = Record<string, unknown>;

/** Alignment bit flags. 0 is centre on both axes. Combine with `|`. */
export const Align = {
  CENTER: 0,
  LEFT: 1,
  RIGHT: 2,
  TOP: 4,
  BOTTOM: 8,
} as const;
export type Alignment = number;

export type BoundsType =
  | 'none'
  | 'stretch'
  | 'scale-inner'
  | 'scale-outer'
  | 'scale-to-width'
  | 'scale-to-height'
  | 'max-only';

export type BlendMode = 'normal' | 'additive' | 'subtract' | 'screen' | 'multiply' | 'lighten' | 'darken';

export type ScaleFilter = 'default' | 'point' | 'bilinear' | 'bicubic' | 'lanczos' | 'area';

/** Pixels removed from each edge of the source, in source pixels, before scaling. */
export interface Crop {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ItemTransform {
  /** Position of the alignment anchor in parent (canvas or group) pixels. */
  position: Vec2;
  /** Scale factors; negative values flip. Ignored for sizing when boundsType !== 'none'. */
  scale: Vec2;
  /** Clockwise degrees about the alignment anchor. */
  rotation: number;
  /** Which point of the item box `position` refers to. */
  alignment: Alignment;
  boundsType: BoundsType;
  /** Where the content sits inside the bounding box when it does not fill it. */
  boundsAlignment: Alignment;
  /** Bounding box size in parent pixels. Used when boundsType !== 'none'. */
  bounds: Vec2;
  crop: Crop;
}

interface SceneItemBase {
  id: Id;
  visible: boolean;
  locked: boolean;
  transform: ItemTransform;
  blendMode: BlendMode;
  scaleFilter: ScaleFilter;
  /** 0..1 multiplied into the item's alpha. */
  opacity: number;
}

/** An instance of a reusable source. Several items may reference one source. */
export interface SourceItem extends SceneItemBase {
  type: 'source';
  sourceId: Id;
}

/** Another scene rendered as an item (scene nesting). Cycles are rejected by commands. */
export interface NestedSceneItem extends SceneItemBase {
  type: 'scene';
  sceneId: Id;
}

/** A group: children are positioned relative to the group's own box. */
export interface GroupItem extends SceneItemBase {
  type: 'group';
  name: string;
  children: SceneItem[];
}

export type SceneItem = SourceItem | NestedSceneItem | GroupItem;

export interface FilterInstance {
  id: Id;
  /** Registry key of a video or audio filter definition. */
  kind: string;
  name: string;
  enabled: boolean;
  settings: SettingsBag;
}

export type MonitoringMode = 'off' | 'monitor-only' | 'monitor-and-output';

export interface AudioStrip {
  /** Linear amplitude multiplier. 1 = 0 dB. */
  volume: number;
  muted: boolean;
  /** -1 (left) .. 1 (right). */
  balance: number;
  forceMono: boolean;
  /** Delay applied to this source's audio, in ms. Negative values are clamped to 0 (see docs/browser-limitations.md). */
  syncOffsetMs: number;
  monitoring: MonitoringMode;
}

export interface Source {
  id: Id;
  name: string;
  /** Registry key of the source type, e.g. 'camera', 'text', or '<pluginId>.<name>' for plugin kinds. */
  kind: string;
  settings: SettingsBag;
  /** Applied in order. Video and audio filters share the list; the registry says which is which. */
  filters: FilterInstance[];
  audio: AudioStrip;
  /** Shape version of `settings` (SourceTypeDefinition.settingsVersion). Absent means 0, i.e. never migrated. */
  settingsVersion?: number;
  /**
   * Run state saved by the runtime (SourceContext.saveRuntimeState): written
   * without undo, not copied by duplicate, not exported in bundles.
   */
  runtimeState?: SettingsBag;
}

export interface Scene {
  id: Id;
  name: string;
  /** Draw order: index 0 is the bottom-most item. UI lists show this reversed. */
  items: SceneItem[];
  filters: FilterInstance[];
}

export interface TransitionConfig {
  id: Id;
  kind: string;
  name: string;
  settings: SettingsBag;
}

export interface VideoSettings {
  width: number;
  height: number;
  fps: number;
}

export interface AudioSettings {
  sampleRate: 44100 | 48000;
  /** Linear amplitude multiplier on the master bus. */
  masterVolume: number;
  masterMuted: boolean;
  /** `sinkId` for the monitoring bus, or null for the system default. */
  monitorDeviceId: string | null;
}

export interface IceServerConfig {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export type VideoCodecPreference = 'auto' | 'vp8' | 'vp9' | 'h264' | 'av1';
export type DegradationPreference = 'balanced' | 'maintain-framerate' | 'maintain-resolution';

/** The ompeer.com control plane; the swarm API URL of new projects and of imported collections. */
export const DEFAULT_SWARM_API_URL = 'https://api.ompeer.com';

/** A BCP-47 tag as the Web Speech API takes it, e.g. `en-US`, `es-419`, `pt-BR`. */
export const CAPTION_LANG_PATTERN = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;
export const CAPTION_LANG_MAX = 35;
export const CAPTION_TEXT_MAX = 500;

/**
 * Automatic captions: generated in the browser with the Web Speech API while live on the swarm and
 * sent to the control plane, which relays them to viewers. No server-side transcription.
 */
export interface CaptionSettings {
  enabled: boolean;
  /** CAPTION_LANG_PATTERN, ≤ CAPTION_LANG_MAX; the browser language by default. */
  lang: string;
  /**
   * The audio-input source the captions are meant to follow; null means the first one. The Web
   * Speech API always listens to the browser's default microphone, so this only drives the UI
   * hint (see docs/architecture.md, "Live captions").
   */
  sourceId: Id | null;
}

export interface StreamingSettings {
  /** 'swarm': the studio segments, encrypts and seeds an ompeer.com P2P swarm (Chromium only). */
  mode: 'p2p' | 'whip' | 'swarm';
  signalingUrl: string;
  iceServers: IceServerConfig[];
  /** 'relay' forces every connection through a TURN server (needs one in iceServers). */
  iceTransportPolicy: 'all' | 'relay';
  maxViewers: number;
  allowGuests: boolean;
  /** Offer one shared guest link for the room besides personal invites. Off by default: it can be forwarded. */
  guestLink: boolean;
  /** Whether people arriving through the shared link wait in the lobby for approval. */
  guestApproval: 'lobby' | 'auto';
  videoKbps: number;
  audioKbps: number;
  codec: VideoCodecPreference;
  degradation: DegradationPreference;
  whipEndpoint: string;
  /** Stored locally in IndexedDB in clear text; see docs/architecture.md (security). */
  whipBearerToken: string;
  /** ohmypeer control plane, e.g. https://api.ompeer.com. */
  swarmApiUrl: string;
  /** `sk_<streamId>.<channelId>.<secret>`. Stored locally in clear text like whipBearerToken. */
  swarmStreamKey: string;
  /**
   * Length of each fMP4 segment published to the swarm. 200 is the delivery v2 profile (200 ms parts,
   * 1 s GOPs, codec Auto = AV1 first, then H.264); 1000/2000 are the legacy H.264 segments.
   */
  swarmSegmentMs: 200 | 1000 | 2000;
  /**
   * Which swarm defaults this document has been brought to (SWARM_DEFAULTS_REVISION). Revision 1 moved the
   * old default of 1 s segments to the 200 ms delivery profile; an explicit 2 s choice is kept.
   */
  swarmDefaultsRevision: number;
  /**
   * H.264 fallback for older devices: with 200 ms parts and an AV1 main, also publish an H.264 rendition for viewers
   * that cannot decode AV1 (when the browser and the server can). Like swarmDefaultsRevision it is repaired on load
   * (absent = 'auto'), so no schema version bump: an older build ignores it.
   */
  swarmH264Fallback: 'auto' | 'off';
  captions: CaptionSettings;
}

export interface RecordingSettings {
  /** A MediaRecorder mime type, or 'auto' to pick the best supported one. */
  mimeType: string;
  videoKbps: number;
  audioKbps: number;
  /** 'opfs' streams chunks to the origin-private file system; 'memory' keeps them in RAM. */
  sink: 'memory' | 'opfs';
}

export interface OutputSettings {
  streaming: StreamingSettings;
  recording: RecordingSettings;
}

export interface PluginRef {
  id: string;
  /** URL of an ES module exporting a PluginModule (plugins/api.ts). */
  url: string;
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// Channel: what the broadcaster tells the audience and the control plane about
// the stream and about themselves (the equivalent of a platform's channel
// dashboard). Pushed to the ompeer.com control plane in swarm mode.
// ---------------------------------------------------------------------------

export const STREAM_TITLE_MAX = 140;
export const GO_LIVE_NOTIFICATION_MAX = 140;
export const STREAM_CATEGORY_MAX = 60;
export const STREAM_TAGS_MAX = 10;
export const STREAM_TAG_PATTERN = /^[A-Za-z0-9_]{1,25}$/;
export const STREAM_LANGUAGE_PATTERN = /^[a-z]{2}(-[A-Z]{2})?$/;
export const CHANNEL_BIO_MAX = 300;
export const CHANNEL_DISPLAY_NAME_MAX = 100;
export const CHANNEL_LINKS_MAX = 5;
export const CHANNEL_LINK_TITLE_MAX = 50;
export const CHANNEL_URL_MAX = 500;
export const ACCENT_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
export const DEFAULT_ACCENT_COLOR = '#9147ff';
/** The ompeer.com portal; where the channel page and the popout chat live. */
export const DEFAULT_PORTAL_URL = 'https://ompeer.com';

/** Audience advisories a broadcaster can attach to a stream. */
export const CONTENT_LABELS = [
  'mature-themes',
  'violence-gore',
  'sexual-themes',
  'drugs-intoxication',
  'gambling',
  'profanity',
] as const;
export type ContentLabel = (typeof CONTENT_LABELS)[number];

/** What viewers see about the current broadcast; sent to the control plane on go-live and on edit. */
export interface StreamInfo {
  /** Shown above the player; the collection name is used when empty. ≤ STREAM_TITLE_MAX. */
  title: string;
  /** Text of the "went live" alert followers get; the title is used when empty. ≤ GO_LIVE_NOTIFICATION_MAX. */
  goLiveNotification: string;
  /** Free text, 1–STREAM_CATEGORY_MAX chars; '' when none. */
  category: string;
  /** ≤ STREAM_TAGS_MAX of STREAM_TAG_PATTERN, unique case-insensitively. */
  tags: string[];
  /** STREAM_LANGUAGE_PATTERN such as `es` or `pt-BR`; '' when unset. */
  language: string;
  contentLabels: ContentLabel[];
  /** The broadcast is a replay, not live content. */
  rerun: boolean;
}

export interface ChannelLink {
  title: string;
  /** https:// only. */
  url: string;
}

/** The channel's public identity: the "About" and "Brand" pages of a channel dashboard. */
export interface ChannelProfile {
  /** Shown instead of the slug; '' keeps the channel's registered name. ≤ CHANNEL_DISPLAY_NAME_MAX. */
  displayName: string;
  /** ≤ CHANNEL_BIO_MAX. */
  bio: string;
  /** ≤ CHANNEL_LINKS_MAX social links. */
  links: ChannelLink[];
  /** `#RRGGBB`; tints the channel page and the generated banner. */
  accentColor: string;
  /** https:// image URLs; '' when unset. */
  avatarUrl: string;
  bannerUrl: string;
  /** Shown in the player while the channel is offline. */
  offlineImageUrl: string;
}

/** How long the control plane holds the stream open for a dropped studio when disconnectProtection is on. */
export const DISCONNECT_PROTECTION_MS = 90_000;
export const SCHEDULE_ENTRIES_MAX = 20;
export const SCHEDULE_DURATION_MIN = 15;
export const SCHEDULE_DURATION_MAX = 1440;
export const SUGGESTED_CHANNELS_MAX = 10;
export const CHANNEL_SLUG_PATTERN = /^[a-z0-9-]{1,50}$/;

/** One recurring slot of the weekly stream schedule, in the broadcaster's time zone. */
export interface ScheduleEntry {
  /** ≤ STREAM_TITLE_MAX; '' means "a stream". */
  title: string;
  /** ≤ STREAM_CATEGORY_MAX; '' when none. */
  category: string;
  /** Minutes after local midnight, 0–1439. */
  startMinutes: number;
  /** SCHEDULE_DURATION_MIN–SCHEDULE_DURATION_MAX. */
  durationMinutes: number;
  /** Weekdays, 0 = Sunday … 6 = Saturday, unique and ascending, at least one. */
  days: number[];
  /** IANA zone the times are written in, e.g. `America/Argentina/Buenos_Aires`. */
  timeZone: string;
}

/** What else the channel page shows ("Featured content"). */
export interface FeaturedContent {
  /** Slugs of channels recommended to viewers, ≤ SUGGESTED_CHANNELS_MAX. */
  suggestedChannels: string[];
  /** 'listed' keeps the order above; 'personal' lets the portal reorder per viewer. */
  suggestedOrder: 'listed' | 'personal';
  /** Show the category of the last stream on the channel page. */
  showRecentCategories: boolean;
  /** The channel page's recommendation panel: the suggested channels, or nothing. */
  panel: 'suggested' | 'none';
}

export const GOAL_TARGET_MAX = 10_000_000;
export const GOAL_TITLE_MAX = 60;

/** A follower goal shown on the channel page ("Manage goals"). */
export interface ChannelGoal {
  kind: 'followers';
  /** 1–GOAL_TARGET_MAX. */
  target: number;
  /** ≤ GOAL_TITLE_MAX; '' shows a default label. */
  title: string;
}

/** Which incoming raids this channel accepts ("Raid settings"). */
export interface RaidSettings {
  /** 'anyone' | 'following' (channels whose owner this channel follows) | 'nobody' (reject all). */
  accept: 'anyone' | 'following' | 'nobody';
  /** Raiding stream must bring at least this many viewers (0 = no minimum). */
  minViewers: number;
  /** ... and at most this many (0 = no maximum). */
  maxViewers: number;
  /** Raiding channel must be at least this old, in days (0 = any). */
  minChannelAgeDays: number;
}

export const FAVOURITE_NAME_MAX = 40;
export const FAVOURITE_NOTE_MAX = 60;
export const FAVOURITES_MAX = 20;

/** Someone the streamer often streams with (Collaboration → "Favourites"), invited in one click. */
export interface ChannelFavourite {
  /** 1–FAVOURITE_NAME_MAX; unique case-insensitively. Becomes the invitation's guest name. */
  name: string;
  /** ≤ FAVOURITE_NOTE_MAX; '' for none. */
  note: string;
}

export const CLIP_SECONDS = [15, 30, 60, 90] as const;
export type ClipSeconds = (typeof CLIP_SECONDS)[number];
export const VOD_RETENTION_DAYS_MAX = 365;
/** Follow ages (minutes) a viewer must have to clip or comment: 0 = any follower … 129600 = 3 months. */
export const FOLLOW_AGE_MINUTES = [0, 10, 30, 60, 1440, 10080, 43200, 129600] as const;
export const CLIP_EXCLUDED_CATEGORIES_MAX = 20;
/** A category slug: lowercase letters, digits and dashes. */
export const CATEGORY_SLUG_PATTERN = /^[a-z0-9-]+$/;
export const CLIP_REACTION_EMOJIS_MAX = 6;
export const DEFAULT_CLIP_REACTION_EMOJIS: readonly string[] = ['❤️', '😂', '😮', '🔥', '👏', '😢'];

/**
 * Clips: the operator's replay buffer ("Make a clip" saves it as a recording) and viewer clips
 * (viewers cut from the segments their player holds and upload them to the control plane).
 */
export interface ClipSettings {
  enabled: boolean;
  /** How far back a clip reaches, at least. */
  seconds: ClipSeconds;
  /** Who may make viewer clips: anyone signed in, or only followers. */
  whoCanClip: 'anyone' | 'following';
  /** Viewers may add captions to their clips. */
  captions: boolean;
  /** Emoji reactions on clips. */
  reactions: boolean;
  /** Comments on clips. */
  comments: boolean;
  /** With whoCanClip 'following': how long (minutes, one of FOLLOW_AGE_MINUTES) the viewer must have followed. */
  minFollowMinutes: number;
  /** Category slugs during which viewers cannot clip, at most CLIP_EXCLUDED_CATEGORIES_MAX. */
  excludedCategories: string[];
  /** Who may comment on clips. */
  commentsWho: 'anyone' | 'following';
  /** With commentsWho 'following': the follow age required, one of FOLLOW_AGE_MINUTES. */
  commentsMinFollowMinutes: number;
  /** 1..CLIP_REACTION_EMOJIS_MAX single emoji viewers react with. */
  reactionEmojis: string[];
}

export interface ChannelSettings {
  info: StreamInfo;
  profile: ChannelProfile;
  schedule: ScheduleEntry[];
  featured: FeaturedContent;
  goal: ChannelGoal | null;
  raids: RaidSettings;
  clips: ClipSettings;
  /** ≤ FAVOURITES_MAX; local to the studio, never sent to the control plane. */
  favourites: ChannelFavourite[];
  /** Saved broadcasts and clips older than this are deleted at start-up; 0 keeps them forever. */
  vodRetentionDays: number;
  /** Record locally for as long as the stream runs ("save past broadcasts"). */
  saveBroadcasts: boolean;
  /**
   * "Disconnect protection": ask the control plane to keep the stream on air for
   * DISCONNECT_PROTECTION_MS after this studio drops, so viewers wait instead of seeing it end.
   */
  disconnectProtection: boolean;
  /** The portal origin, e.g. https://ompeer.com: channel page links and the Stream Manager chat. */
  portalUrl: string;
}

export const PROJECT_SCHEMA_VERSION = 3;

/** See StreamingSettings.swarmDefaultsRevision. Kept outside the schema version so an older build still opens the project. */
export const SWARM_DEFAULTS_REVISION = 1;

export interface Project {
  schemaVersion: number;
  id: Id;
  name: string;
  createdAt: number;
  updatedAt: number;
  video: VideoSettings;
  audio: AudioSettings;
  output: OutputSettings;
  channel: ChannelSettings;
  sources: Record<Id, Source>;
  scenes: Record<Id, Scene>;
  sceneOrder: Id[];
  /** The scene shown on program when the project is opened. */
  currentSceneId: Id;
  transitions: Record<Id, TransitionConfig>;
  transitionOrder: Id[];
  currentTransitionId: Id;
  transitionDurationMs: number;
  /** action id -> key chords such as "Ctrl+Shift+KeyR" (KeyboardEvent.code based). */
  hotkeys: Record<string, string[]>;
  plugins: PluginRef[];
  /** Per-plugin persisted data, keyed by plugin id. */
  pluginData: Record<string, SettingsBag>;
}
